import React, { Component } from "react";
import {
  createCompany,
  fetchCompanies,
  resetCompanyAlert,
  updateCompanyAlert,
  updateCompanyInformation,
} from "../../actions/companies";
import { nextSort, sortRows } from "../sortable";

import AdminFilterHeader from "../../components/AdminFilterHeader/AdminFilterHeader";
import { AdminTable } from "../AdminTable";
import { Button } from "reactstrap";
import CompanyModal from "./CompanyModal";
import CopyButton from "../../components/copyButton";
import PropTypes from "prop-types";
import _ from "lodash";
import { connect } from "react-redux";
import { hasAdminAccess } from "../../services/roles";
import { requestFetchAllContractInfos } from "../../services/backendRequests";
import { resetCompany } from "../../actions/company";

// Same rule as the users search on the back: hex and dashes, at least a uuid's first
// segment, so a short query stays a name search.
const ID_QUERY = /^[0-9a-f-]{8,36}$/i;

class Companies extends Component {
  constructor(props) {
    super(props);
    this.props.resetCompany();
    this.state = {
      updatedCompany: {},
      isCompanyOpenedModal: false,
      search: "",
      campaigns: [],
      campaignsError: false,
      sortKey: "name",
      sortDir: "asc",
    };
    this.openCompanyModalForCreation =
      this.openCompanyModalForCreation.bind(this);
  }

  handleFilterChange = (event) => {
    const search = event.target.value;
    this.setState({ search });
    if (ID_QUERY.test(search.trim())) this.loadCampaigns();
  };

  // Campaign and site ids, so a uuid pasted from a log or a ticket finds its company.
  // Fetched on the first id-like search only: a name search needs nothing but the companies.
  // A failure is shown and the next keystroke retries, never read as "no result".
  loadCampaigns() {
    if (this.campaignsRequested) return;
    this.campaignsRequested = true;
    requestFetchAllContractInfos(this.props.user)
      .then((campaigns) => this.setState({ campaigns, campaignsError: false }))
      .catch(() => {
        this.campaignsRequested = false;
        this.setState({ campaignsError: true });
      });
  }

  handleSort = (column) => {
    this.setState((prev) => nextSort(prev, column));
  };

  resetState() {
    this.setState({
      updatedCompany: {},
      isCompanyOpenedModal: false,
    });
  }

  componentDidMount() {
    this.props.handleRefresh();
  }

  // Company ids owning a campaign, a site or being the company whose id starts with the
  // needle.
  companiesMatchingId(needle) {
    if (!ID_QUERY.test(needle)) return new Set();
    const hit = (id) => id.toLowerCase().startsWith(needle);
    const ids = new Set(
      this.state.campaigns
        .filter((c) => hit(c.id) || (c.site_ids ?? []).some(hit))
        .map((c) => c.company.id)
    );
    this.props.companies.forEach((c) => hit(c.id) && ids.add(c.id));
    return ids;
  }

  /** COMPANY MODAL */

  openCompanyModalForCreation(event) {
    this.setState({
      updatedCompany: {
        name: "",
      },
      isCompanyOpenedModal: true,
    });
  }

  openCompanyModalForUpdate(event, company) {
    event.stopPropagation();
    this.setState({
      updatedCompany: company,
      isCompanyOpenedModal: true,
    });
  }

  submitCompanyModal(company) {
    if (this.checkCompanyUnicity(company)) {
      if (company.id) this.props.onUpdateCompany(company);
      else this.props.onCreateCompany(company);

      this.resetState();
    }
  }

  checkCompanyUnicity(company) {
    const companiesFiltered = _.filter(
      this.props.companies,
      (item) => item.id !== company.id
    );
    if (_.filter(companiesFiltered, { name: company.name }).length > 0) {
      this.props.updateCompanyAlert("Company name must be unique.");
      return false;
    }
    return true;
  }

  cancelCompanyModal() {
    this.props.resetCompanyAlert();
    this.resetState();
  }

  handleCompanyRowClick(company) {
    this.props.history.push(`/administration/company/${company.id}`);
  }

  render() {
    const search = this.state.search.trim().toLowerCase();
    const idMatches = this.companiesMatchingId(search);
    const filteredCompanies =
      search === ""
        ? this.props.companies
        : this.props.companies.filter(
            (company) =>
              company.name.toLowerCase().includes(search) ||
              idMatches.has(company.id)
          );
    const companies = sortRows(
      filteredCompanies,
      this.state.sortKey,
      this.state.sortDir,
      (company) => company.name || ""
    );
    const columns = [
      { key: "name", label: "Company", className: "w-75", sortable: true },
      {
        key: "actions",
        label: "Actions",
        className: "w-25",
        cell: (company) => (
          <div className="admin-actions">
            <CopyButton value={company.id} datatip="Copy company UUID" />
            {hasAdminAccess(this.props.user.self) && (
              <Button
                data-tooltip-id="tooltip"
                data-tooltip-content="Edit company information"
                color="link"
                onClick={(event) =>
                  this.openCompanyModalForUpdate(event, company)
                }
              >
                <i className="fa-solid fa-lg fa-pencil" />
              </Button>
            )}
            <Button
              data-tooltip-id="tooltip"
              data-tooltip-content="View upload sessions"
              color="link"
              onClick={(e) => {
                e.stopPropagation();
                this.props.history.push(
                  "/uploadsessions?company=" + company.id
                );
              }}
            >
              <i className="fa-solid fa-lg fa-upload" />
            </Button>
          </div>
        ),
      },
    ];
    return (
      <div className="mt-3 ps-3">
        <CompanyModal
          alert={this.props.companyUpdateAlert}
          isOpen={
            this.state.isCompanyOpenedModal ||
            this.props.companyUpdateAlert !== ""
          }
          onClose={() => {
            this.cancelCompanyModal();
          }}
          onSubmit={(company) => this.submitCompanyModal(company)}
          company={this.state.updatedCompany}
        />
        <AdminFilterHeader
          adminOnly
          onButtonClick={this.openCompanyModalForCreation}
          onFilterChange={this.handleFilterChange}
          onRefresh={this.props.handleRefresh}
          refreshing={this.props.refreshing}
          buttonName="New company"
          search={this.state.search}
        />
        {this.state.campaignsError && ID_QUERY.test(search) && (
          <div className="text-danger small mb-2">
            Campaign and site ids could not be loaded, only company names and
            ids are searched.
          </div>
        )}

        <AdminTable
          columns={columns}
          rows={companies}
          loading={this.props.refreshing}
          sortKey={this.state.sortKey}
          sortDir={this.state.sortDir}
          onSort={this.handleSort}
          onRowClick={(company) => this.handleCompanyRowClick(company)}
        />
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    refreshing: state.companies.table.refreshing,
    companyUpdateAlert: state.companies.updateAlert,
    companies: state.companies.all,
    user: state.user,
  };
}

const callbacks = {
  handleRefresh: fetchCompanies,
  resetCompany,
  updateCompanyAlert,
  onUpdateCompany: updateCompanyInformation,
  onCreateCompany: createCompany,
  resetCompanyAlert,
};

export default connect(mapStateToProps, callbacks)(Companies);

Companies.propTypes = {
  companies: PropTypes.array.isRequired,
  handleRefresh: PropTypes.func.isRequired,
  refreshing: PropTypes.bool.isRequired,
  resetCompany: PropTypes.func.isRequired,
  companyUpdateAlert: PropTypes.string.isRequired,
  resetCompanyAlert: PropTypes.func.isRequired,
  onUpdateCompany: PropTypes.func.isRequired,
  onCreateCompany: PropTypes.func.isRequired,
  updateCompanyAlert: PropTypes.func.isRequired,
};
